import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { loadKeepSignedInPreference, loadSavedEmail } from '../auth/authPreferences';
import AuthPrivacyNote from './AuthPrivacyNote';
import KeepSignedInField from './KeepSignedInField';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(5, 0, 10, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2050;
  padding: 20px;
`;

const Card = styled(motion.div)`
  width: min(440px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  background: rgba(20, 8, 36, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  padding: 28px 24px;
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.3;
  background: linear-gradient(135deg, #ff00aa, #00f5ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Body = styled.p`
  margin: 0 0 20px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.95rem;
  line-height: 1.55;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 999px;
  padding: 12px 16px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;
  margin-bottom: 10px;

  &:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(ActionButton)`
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
  margin-top: 4px;
`;

const TextButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  font-size: 0.9rem;
  padding: 8px 0;
  width: 100%;
  text-align: center;

  &:hover {
    color: #fff;
  }
`;

const LinkButton = styled(TextButton)`
  color: rgba(0, 245, 255, 0.95);
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.95rem;
  outline: none;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: rgba(0, 245, 255, 0.6);
    box-shadow: 0 0 0 3px rgba(0, 245, 255, 0.15);
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: #ff7a7a;
  font-size: 0.85rem;
`;

function getFriendlyError(error) {
  if (!error) return '';
  switch (error.code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
      return 'No account found with that email.';
    case 'auth/wrong-password':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'That email is already in use. Try logging in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/unauthorized-domain':
      return 'Sign-in is not enabled for this website domain yet.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked. Allow pop-ups for this site and try again.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled yet. Try email or Google instead.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

function BucketListAuthModal({ open, onMaybeLater, onSuccess }) {
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [view, setView] = useState('prompt');
  const [emailMode, setEmailMode] = useState('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [keepSignedIn, setKeepSignedIn] = useState(() => loadKeepSignedInPreference());

  const friendlyError = useMemo(() => getFriendlyError(error), [error]);

  useEffect(() => {
    if (!open) {
      setView('prompt');
      setEmailMode('signup');
      setError(null);
      setSubmitting(false);
      return;
    }
    setKeepSignedIn(loadKeepSignedInPreference());
    setEmail(loadSavedEmail());
  }, [open]);

  if (!open) return null;

  const finishAuth = () => {
    onSuccess?.();
  };

  const runAuth = async (fn) => {
    setSubmitting(true);
    setError(null);
    try {
      await fn();
      finishAuth();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = () =>
    runAuth(() => signInWithGoogle({ rememberMe: keepSignedIn }));

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    await runAuth(async () => {
      if (emailMode === 'signup') {
        await signUp({ name, email, password }, { rememberMe: keepSignedIn });
      } else {
        await signIn(email, password, { rememberMe: keepSignedIn });
      }
    });
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onMaybeLater?.();
    }
  };

  return (
    <Overlay
      onClick={handleOverlayClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Card
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Title>
            {view === 'email' && emailMode === 'login'
              ? 'Welcome back'
              : 'Build your immersive bucket list.'}
          </Title>
          <CloseButton type="button" aria-label="Close" onClick={onMaybeLater}>
            ×
          </CloseButton>
        </Header>

        {view === 'prompt' ? (
          <>
            <Body>
              Create a free account to save experiences and get personalized recommendations.
            </Body>

            <KeepSignedInField
              id="bucket-keep-signed-in-prompt"
              checked={keepSignedIn}
              onChange={setKeepSignedIn}
            />

            <ActionButton type="button" disabled={submitting} onClick={handleGoogleSignIn}>
              Continue with Google
            </ActionButton>
            <ActionButton
              type="button"
              disabled={submitting}
              onClick={() => {
                setView('email');
                setEmailMode('signup');
                setError(null);
              }}
            >
              Continue with Email
            </ActionButton>

            {friendlyError && <ErrorText>{friendlyError}</ErrorText>}

            <AuthPrivacyNote />
            <LinkButton
              type="button"
              onClick={() => {
                setView('email');
                setEmailMode('login');
                setError(null);
              }}
            >
              Already have an account? Log In
            </LinkButton>
            <TextButton type="button" onClick={onMaybeLater}>
              Maybe later
            </TextButton>
          </>
        ) : (
          <>
            {emailMode === 'signup' && (
              <Body style={{ marginBottom: 16 }}>
                Create your free account to start building your bucket list.
              </Body>
            )}

            <Form onSubmit={handleEmailSubmit}>
              {emailMode === 'signup' && (
                <div>
                  <Label htmlFor="bucket-name">Name</Label>
                  <Input
                    id="bucket-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="bucket-email">Email</Label>
                <Input
                  id="bucket-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="bucket-password">Password</Label>
                <Input
                  id="bucket-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a password"
                  autoComplete={emailMode === 'signup' ? 'new-password' : 'current-password'}
                  required
                />
              </div>
              <KeepSignedInField
                id="bucket-keep-signed-in-email"
                checked={keepSignedIn}
                onChange={setKeepSignedIn}
              />
              {friendlyError && <ErrorText>{friendlyError}</ErrorText>}
              <PrimaryButton type="submit" disabled={submitting}>
                {submitting ? 'Please wait…' : emailMode === 'signup' ? 'Create account' : 'Log In'}
              </PrimaryButton>
              <AuthPrivacyNote />
            </Form>

            <LinkButton
              type="button"
              onClick={() => {
                setEmailMode(emailMode === 'signup' ? 'login' : 'signup');
                setError(null);
              }}
            >
              {emailMode === 'signup' ? 'Already have an account? Log In' : 'New here? Create an account'}
            </LinkButton>
            <TextButton type="button" onClick={() => setView('prompt')}>
              Back
            </TextButton>
            <TextButton type="button" onClick={onMaybeLater}>
              Maybe later
            </TextButton>
          </>
        )}
      </Card>
    </Overlay>
  );
}

export default BucketListAuthModal;
